import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../App";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (error) {
      // Provide iOS-friendly error message (no silent failures on Safari/Chrome iOS)
      let msg = "Login failed";
      if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('Network')) {
        msg = "Network error — please check your connection and try again.";
      } else if (error.message) {
        msg = error.message;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" className="flex items-center gap-3 mb-10" data-testid="logo-link">
            <Trophy className="w-8 h-8 text-[#FFD700]" />
            <span className="font-['Playfair_Display'] text-xl font-bold text-white">JackAndJill</span>
          </Link>

          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
            Welcome Back
          </h1>
          <p className="text-[#A1A1AA] mb-8">
            Sign in to manage your dance competitions
          </p>

          {/* Google Sign In — toggled by REACT_APP_ENABLE_GOOGLE_AUTH */}
          {process.env.REACT_APP_ENABLE_GOOGLE_AUTH === "true" && (
            <>
              <Button 
                variant="outline" 
                className="w-full btn-outline py-4 mb-6 flex items-center justify-center gap-3"
                onClick={loginWithGoogle}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#09090b] text-[#52525B]">or sign in with email</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <Label htmlFor="login-email" className="text-[#A1A1AA] mb-2 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  id="login-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  placeholder="you@example.com"
                  className="pl-12 h-12 bg-black/20 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#FFD700]/50"
                  required
                  autoComplete="email"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="login-password" className="text-[#A1A1AA] mb-2 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                <Input
                  id="login-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 pr-12 h-12 bg-black/20 border-white/10 text-white placeholder:text-[#52525B] focus:border-[#FFD700]/50"
                  required
                  autoComplete="current-password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-gold py-4 text-lg rounded-lg"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <p className="text-center text-[#A1A1AA] mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#FFD700] hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>

          {/* Forgot password support */}
          <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/5 text-xs text-[#A1A1AA] leading-relaxed" data-testid="forgot-password-help">
            <p className="text-white text-sm font-medium mb-2">Forgot your password?</p>
            <p>
              Please contact us on Instagram{" "}
              <a
                href="https://instagram.com/jackandjillscore"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FFD700] hover:underline font-medium"
                data-testid="forgot-instagram-link"
              >
                @jackandjillscore
              </a>{" "}
              <span className="text-[#52525B]">(follow our page first, then send your message)</span> or via email at{" "}
              <a
                href="mailto:jackandjillscoring@gmail.com"
                className="text-[#FFD700] hover:underline font-medium"
                data-testid="forgot-email-link"
              >
                jackandjillscoring@gmail.com
              </a>.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1600')` 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/50 to-transparent" />
      </div>
    </div>
  );
}
